import moment from "moment";
import React from "react";

import "./invoice.css";
import price from "../../helpers/calculatePrice";
import roundOffTotal from "../../helpers/roundOffTotal";

let exclusiveGST;
let roundOffType;

const Header = ({
  orderNumber,
  branchOrderNumber,
  customerName,
  customerMobile,

  resName,
  orderDate,
  paymentType,
  logo,
  tableNumber,
  orderType,
  gstNumber,
  fssaiNumber,
  branchAddress,
}) => {
  return (
    <>
      <div className="centered">
        {logo && <img src={logo} className="logo" />}
        <p>{resName}</p>
        {branchAddress && <p className="gstNumber">{branchAddress}</p>}
        {fssaiNumber && <p className="gstNumber">FSSAI Number {fssaiNumber}</p>}
      </div>

      <div className="info">
        {customerName && (
          <div className="subinfo">
            <p>
              Name : {customerName}{" "}
              {customerMobile ? `(${customerMobile})` : ""}
            </p>
          </div>
        )}
        <div className="subinfo">
          <p>{branchOrderNumber}</p>
          <p>{moment(orderDate).format("DD/MM/YYYY HH:mm")}</p>
        </div>
        <div className="subinfo">
          <p>{orderType}</p>
          <p>{orderNumber}</p>

          <p>{tableNumber}</p>

          <p>{paymentType}</p>
        </div>
      </div>
    </>
  );
};

const renderRow = (data, value) => {
  return value ? (
    <>
      <tr>
        <td colspan="3" className={data.titleClass}>
          {data.title}
        </td>
        <td colspan="3" className={data.valueClass}>
          {data.hasCurrency ? "₹" : " "}{" "}
          {data.key === "roundOff" && value > 0 ? "+" : ""}
          {value}
        </td>
      </tr>
    </>
  ) : (
    ""
  );
};

const renderItem = (value, index, i, tableType) => {
  return (
    <React.Fragment key={index}>
      <tr>
        <td className="pro">{value.itemName}</td>
        <td className="quantity">{value.quantity}</td>
        <td className="price">
          {price(tableType?.tableType, value) || value.itemPrice}
        </td>
        <td className="amount">
          {(value.quantity * price(tableType?.tableType, value)).toFixed(2) ||
            value.itemTotal}
        </td>
      </tr>
    </React.Fragment>
  );
};

const renderTaxItem = (orderData, itemTotal, tableType, chargeDifference) => {
  let itemsTotal = 0;
  let itemPrise = 0;
  orderData.map((value) => {
    itemPrise =
      value.quantity * price(tableType?.tableType, value) || value.itemTotal;
    itemsTotal += itemPrise + chargeDifference * (itemPrise / itemTotal);
  });
  let gstAmount =
    ((orderData[0]?.cgst || 0) + (orderData[0]?.sgst || 0)) / 100 +
    (exclusiveGST ? 0 : 1);
  let taxAmount = exclusiveGST
    ? itemsTotal.toFixed(2)
    : (itemsTotal / gstAmount).toFixed(2);

  return (
    <React.Fragment>
      <tr>
        <td className="pro">{taxAmount}</td>
        <td className="gst">
          {exclusiveGST
            ? ((taxAmount * gstAmount) / 2).toFixed(2)
            : ((itemsTotal - taxAmount) / 2).toFixed(2)}
        </td>
        <td className="gst">
          {exclusiveGST
            ? ((taxAmount * gstAmount) / 2).toFixed(2)
            : ((itemsTotal - taxAmount) / 2).toFixed(2)}
        </td>
      </tr>
    </React.Fragment>
  );
};

const ProductTable = ({ orderData, tableType }) => {
  // Compute all receipt totals fresh from raw fields.
  // This ensures Grand Total and Round Off always match the Order Summary,
  // even if the DB grandTotal is stale (e.g. saved with a different GST mode).
  const receiptSubTotal = parseFloat(orderData.itemsTotal || 0);
  const receiptDiscount = parseFloat(orderData.discount || 0);
  const receiptOtherCharges = parseFloat(orderData.otherCharges || 0);
  const receiptGst = exclusiveGST
    ? parseFloat(orderData.cgstCharges || 0) +
      parseFloat(orderData.sgstCharges || 0)
    : 0;
  const receiptUnrounded =
    receiptSubTotal - receiptDiscount + receiptOtherCharges + receiptGst;
  const receiptGrandTotal = roundOffTotal(receiptUnrounded, roundOffType);
  const receiptRoundOff = receiptGrandTotal - receiptUnrounded;

  const printfooters = [
    {
      title: "Sub Total",
      hasCurrency: true,
      key: "itemsTotal",
      titleClass: "subtotal",
      valueClass: "subtotalamount",
      condition: (data) =>
        data.cgstCharges + data.sgstCharges !== 0 ||
        data.discount !== 0 ||
        data.tablePrice !== 0 ||
        data.otherCharges !== 0,
    },

    {
      title: "Discount(-)",
      hasCurrency: true,
      key: "discount",
      titleClass: "discount",
      valueClass: "discount",
      condition: (data) => data.discount && data.discount !== 0,
    },

    ...(exclusiveGST
      ? [
          {
            title: "GST(+)",
            key: "exclusiveGST",
            hasCurrency: true,
            titleClass: "discount",
            valueClass: "discount",
            condition: (data) => data.cgstCharges + data.sgstCharges !== 0,
          },
        ]
      : []),

    {
      title: "Table Price(+)",
      hasCurrency: true,
      key: "tablePrice",
      titleClass: "tablePrice",
      valueClass: "tablePriceamount",
      condition: (data) => data.tablePrice && data.tablePrice !== 0,
    },

    {
      title: "Charges(+)",
      hasCurrency: true,
      key: "otherCharges",
      titleClass: "discount",
      valueClass: "discount",
      condition: (data) => data.otherCharges && data.otherCharges !== 0,
    },

    {
      title: "Round off",
      key: "roundOff",
      titleClass: "discount",
      valueClass: "discount",
      condition: () => parseFloat(receiptRoundOff.toFixed(2)) !== 0,
    },

    {
      title: "Grand Total",
      hasCurrency: true,
      key: "grandTotal",
      titleClass: "grand",
      valueClass: "grandtotal",
      alwaysShow: true,
    },
  ];
  return (
    <table className="postable">
      <thead>
        <tr>
          <th className="pro">Item</th>
          <th className="quantity">Qty</th>
          <th className="price">Price</th>
          <th className="amount">Amt</th>
        </tr>
      </thead>
      <tbody>
        {orderData.orderItems
          .filter((value) => value.quantity > 0)
          .map((value, index) => {
            const i = ++index;
            return renderItem(value, index, i, tableType);
          })}

        {printfooters.map((foot, footIndex) => {
          // Check if this row should be displayed
          if (
            !foot.alwaysShow &&
            foot.condition &&
            !foot.condition(orderData)
          ) {
            return null;
          }

          if (foot.key === "roundOff") {
            return renderRow(
              foot,
              parseFloat(receiptRoundOff.toFixed(2)).toFixed(2),
            );
          }
          if (foot.key === "grandTotal") {
            // Always use the freshly calculated grand total, not the stale DB value.
            return renderRow(foot, receiptGrandTotal.toFixed(2));
          }
          if (foot.key === "exclusiveGST") {
            return renderRow(foot, receiptGst.toFixed(2));
          }
          return renderRow(foot, orderData[foot.key].toFixed(2));
        })}
      </tbody>
    </table>
  );
};

const TaxSummary = ({
  orderData,
  itemTotal,
  tableType,
  gst,
  chargeDifference,
}) => {
  return (
    <table className="postable">
      <thead>
        <tr>
          <th className="pro">Taxable Amt</th>
          <th className="gst">{`CGST@${gst}%`}</th>
          <th className="gst">{`SGST@${gst}%`}</th>
        </tr>
      </thead>
      <tbody>
        {renderTaxItem(orderData, itemTotal, tableType, chargeDifference)}
      </tbody>
    </table>
  );
};

const Invoice = React.forwardRef(
  (
    {
      restaurant,
      orderData,
      count,
      logo,
      gstNumber,
      fssaiNumber,
      receiptMessage,
      branchAddress,
      printSetting,
      tableType,
    },
    ref,
  ) => {
    const {
      branchOrderNumber,
      customerName,
      customerMobile,

      orderNumber,
      paymentType,
      updatedAt,
      tableNumber,
      orderType,
    } = orderData;
    exclusiveGST = printSetting.exclusiveGST;
    roundOffType = printSetting?.lowerRoundOff
      ? "lowerRoundOff"
      : printSetting?.upperRoundOff
        ? "upperRoundOff"
        : "standardRoundOff";
    const gstMap = new Map();
    const chargeDifference = -(orderData?.discount || 0);
    let itemTotal = 0;
    orderData.orderItems.map((item) => {
      if (!gstMap.has(item.cgst)) {
        gstMap.set(item.cgst, []);
      }

      let itemList = gstMap.get(item.cgst);
      itemList.push(item);
      itemTotal +=
        item.quantity * price(tableType?.tableType, item) || item.itemTotal;
      gstMap.set(item.cgst, itemList);
    });

    const keys = [...gstMap.keys()];

    return (
      <div className="ticket" ref={ref}>
        <Header
          resName={restaurant}
          branchOrderNumber={branchOrderNumber}
          orderNumber={orderNumber}
          customerName={printSetting.enableCustomer && customerName}
          customerMobile={customerMobile}
          paymentType={paymentType}
          orderDate={updatedAt}
          tableNumber={tableNumber}
          orderType={orderType}
          logo={logo}
          gstNumber={gstNumber}
          fssaiNumber={fssaiNumber}
          branchAddress={branchAddress}
        />
        <ProductTable orderData={orderData} tableType={tableType} />

        {gstNumber && (
          <div className="centered" style={{ paddingTop: "40px" }}>
            <p>{"Tax Summary"}</p>
            <p className="gstNumber">GST {gstNumber}</p>
          </div>
        )}

        {gstNumber &&
          keys.map((key) => {
            return (
              <TaxSummary
                orderData={gstMap.get(key)}
                tableType={tableType}
                chargeDifference={chargeDifference || 0}
                itemTotal={itemTotal}
                gst={key}
              />
            );
          })}
        {receiptMessage && (
          <p className="centered">
            <p>{receiptMessage}</p>
          </p>
        )}
      </div>
    );
  },
);

class BillComponent extends React.Component {
  render() {
    const {
      logo,
      customAction,
      orderData,
      count,
      restaurant,
      gstNumber,
      fssaiNumber,
      receiptMessage,
      branchAddress,
      printSetting,
      tableType,
    } = this.props;
    return orderData ? (
      <Invoice
        count={count}
        restaurant={restaurant}
        orderData={orderData}
        logo={logo}
        gstNumber={gstNumber}
        fssaiNumber={fssaiNumber}
        receiptMessage={receiptMessage}
        branchAddress={branchAddress}
        printSetting={printSetting}
        tableType={tableType}
      />
    ) : (
      <div className={`invoice-box invoice-box-hide" `}>No Data found</div>
    );
  }
}
export default BillComponent;
